import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, ChevronRight, ChevronDown, Users, Wallet, Map as MapIcon, Building2, UtensilsCrossed, Ticket, Link2, Mail, Zap, CalendarClock } from 'lucide-react';
import { api } from '../lib/api';
import { useOnb, onbIndex, onbKey, rupiah } from '../lib/onboarding';
import StampRallyAdmin from './StampRallyAdmin';

const EVENT_DAY = '2026-12-12';
const SRC_TAB = { creator: 'registrations', manual: 'registrations', brand: 'corporate', fnb: 'fnb', lineup: 'lineup' };
const LEVEL = {
  urgent: { icon: AlertTriangle, cls: 'border-red-500/40 bg-red-500/10', ic: 'text-red-400', badge: 'bg-red-500 text-white', label: 'Urgent' },
  warn: { icon: AlertCircle, cls: 'border-amber-400/40 bg-amber-400/10', ic: 'text-amber-300', badge: 'bg-amber-400 text-black', label: 'Segera' },
  info: { icon: Info, cls: 'border-sky-400/30 bg-sky-400/5', ic: 'text-sky-300', badge: 'bg-sky-400/80 text-black', label: 'Info' },
};
const SHOW = 4;

function daysToEvent() {
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${EVENT_DAY}T00:00:00`) - today) / 86400000);
}
const topTab = (docs) => {
  const c = {};
  docs.forEach((d) => { const t = SRC_TAB[d.src] || 'registrations'; c[t] = (c[t] || 0) + 1; });
  return Object.entries(c).sort((a, b) => b[1] - a[1])[0]?.[0] || 'registrations';
};

export default function HomeCommand({ regs = [], corporate = [], fnb = [], setTab }) {
  const { items } = useOnb();
  const [pp, setPp] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [all, setAll] = useState(false);
  useEffect(() => { api.get('/passport/admin').then(({ data }) => setPp(data)).catch(() => setPp(null)); }, [showStamp]);
  const dLeft = daysToEvent();

  const s = useMemo(() => {
    const docs = (items || []).filter((d) => d.status !== 'cancelled');
    const idx = onbIndex(docs);
    const pend = (list) => list.filter((r) => (r.status || 'pending') === 'pending').length;
    const notInvited = (list, src) => list.filter((r) => (r.status || 'pending') === 'approved' && !(idx[onbKey(src, r.id)] && idx[onbKey(src, r.id)].invited_at)).length;
    const dp = docs.filter((d) => d.status === 'dp');
    const awaiting = docs.filter((d) => d.status === 'awaiting_payment');
    const paidish = docs.filter((d) => d.status === 'paid' || d.status === 'dp');
    return {
      pendCreator: pend(regs), pendBrand: pend(corporate), pendFnb: pend(fnb),
      niCreator: notInvited(regs, 'creator'), niBrand: notInvited(corporate, 'brand'), niFnb: notInvited(fnb, 'fnb'),
      review: docs.filter((d) => d.status === 'payment_review'), expired: docs.filter((d) => d.a1_expired && !d.a1_submitted_at),
      dp, awaiting, noBooth: paidish.filter((d) => !d.booth_label), noLogo: paidish.filter((d) => !(d.assets && d.assets.logo_url)),
      paid: docs.filter((d) => d.status === 'paid').length,
      dpDue: dp.reduce((n, d) => n + ((d.invoice && d.invoice.due) || 0), 0),
      awaitDue: awaiting.reduce((n, d) => n + ((d.invoice && d.invoice.due) || 0), 0),
    };
  }, [items, regs, corporate, fnb]);

  const alerts = [];
  const add = (level, title, desc, action, onClick) => alerts.push({ level, title, desc, action, onClick });
  if (s.review.length) add('urgent', `${s.review.length} bukti bayar menunggu dicek`, 'Cocokkan dengan mutasi rekening, lalu konfirmasi Lunas/DP.', 'Cek', () => setTab(topTab(s.review)));
  const pendTotal = s.pendCreator + s.pendBrand + s.pendFnb;
  if (pendTotal) {
    const parts = [s.pendCreator && `${s.pendCreator} Creator`, s.pendBrand && `${s.pendBrand} Brand Booth`, s.pendFnb && `${s.pendFnb} F&B`].filter(Boolean).join(', ');
    add(pendTotal >= 5 ? 'urgent' : 'warn', `${pendTotal} pendaftar menunggu review`, parts, 'Review', () => setTab(s.pendCreator ? 'registrations' : s.pendBrand ? 'corporate' : 'fnb'));
  }
  if (pp && pp.config) {
    const c = pp.config;
    if (c.enabled && c.test_mode && dLeft <= 3 && dLeft >= -1) add('urgent', 'Stamp Rally masih mode uji coba', 'Matikan mode uji coba sebelum event.', 'Buka', () => setShowStamp(true));
    else if (!c.enabled && dLeft <= 14 && dLeft >= 0) add('warn', 'Stamp Rally belum diaktifkan', `H-${dLeft}. Isi hadiah lalu aktifkan program.`, 'Buka', () => setShowStamp(true));
    const sm = pp.summary || {};
    if (sm.flagged) add('warn', `${sm.flagged} peserta Stamp Rally perlu dicek`, 'Scan terlalu cepat.', 'Cek', () => setShowStamp(true));
    const low = (sm.rewards || []).filter((r) => r.stock > 0 && r.left <= Math.max(1, r.stock * 0.1));
    if (low.length) add('urgent', 'Stok hadiah menipis', low.map((r) => `${r.name} (${r.left})`).join(', '), 'Buka', () => setShowStamp(true));
  }
  if (s.noBooth.length) add('warn', `${s.noBooth.length} tenant bayar, belum dapat booth`, s.noBooth.slice(0, 3).map((d) => d.name).join(', ') + (s.noBooth.length > 3 ? ', ...' : ''), 'Floorplan', () => setTab('floorplan'));
  if (s.expired.length) add('warn', `${s.expired.length} tenant lewat batas Form A1`, 'Perpanjang atau hubungi via WA.', 'Lihat', () => setTab(topTab(s.expired)));
  const niTotal = s.niCreator + s.niBrand + s.niFnb;
  if (niTotal) add('warn', `${niTotal} approved belum diundang`, 'Kirim link portal Form A1 + PKS.', 'Undang', () => setTab(s.niCreator ? 'registrations' : s.niBrand ? 'corporate' : 'fnb'));
  if (s.dp.length) add('info', `${s.dp.length} tenant DP, sisa ${rupiah(s.dpDue)}`, 'Ingatkan pelunasan.', 'Lihat', () => setTab(topTab(s.dp)));
  if (s.awaiting.length) add('info', `${s.awaiting.length} menunggu bayar (${rupiah(s.awaitDue)})`, 'Kirim reminder WA.', 'Lihat', () => setTab(topTab(s.awaiting)));
  if (s.noLogo.length) add('info', `${s.noLogo.length} tenant belum upload logo`, 'Untuk Line Up & konten IG.', 'Lihat', () => setTab(topTab(s.noLogo)));
  const order = { urgent: 0, warn: 1, info: 2 };
  alerts.sort((a, b) => order[a.level] - order[b.level]);
  const urgentN = alerts.filter((a) => a.level === 'urgent').length;
  const shown = all ? alerts : alerts.slice(0, SHOW);

  const tiles = [
    { icon: Users, label: 'Review Creator', sub: `${s.pendCreator} pending`, n: s.pendCreator, go: () => setTab('registrations') },
    { icon: Wallet, label: 'Cek bukti bayar', sub: `${s.review.length} menunggu`, n: s.review.length, hot: true, go: () => setTab(s.review.length ? topTab(s.review) : 'registrations') },
    { icon: MapIcon, label: 'Floorplan', sub: s.noBooth.length ? `${s.noBooth.length} tanpa booth` : 'Blok booth', n: s.noBooth.length, go: () => setTab('floorplan') },
    { icon: Ticket, label: 'Stamp Rally', sub: pp && pp.config ? (pp.config.enabled ? (pp.config.test_mode ? 'Mode uji' : 'Aktif') : 'Belum aktif') : 'Passport', go: () => setShowStamp(true) },
    { icon: Building2, label: 'Brand Booth', sub: `${s.pendBrand} pending`, n: s.pendBrand, go: () => setTab('corporate') },
    { icon: UtensilsCrossed, label: 'F&B', sub: `${s.pendFnb} pending`, n: s.pendFnb, go: () => setTab('fnb') },
    { icon: Link2, label: 'Short URL', sub: 'Link kampanye', go: () => setTab('shortlinks') },
    { icon: Mail, label: 'Email', sub: 'Status kirim', go: () => setTab('email') },
  ];

  return (
    <div className="tv-card p-4 mb-6 space-y-3" data-testid="home-command">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="outline-title text-xl inline-flex items-center gap-2"><Zap className="w-4 h-4 text-[#F5C518]" /> Pusat Kendali</h3>
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/80"><CalendarClock className="w-3 h-3 text-[#F5C518]" /> {dLeft > 0 ? `H-${dLeft} · 12 Des 2026` : dLeft >= -1 ? 'Hari event!' : 'Event selesai'}</span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">{s.paid} lunas</span>
          {urgentN > 0 && <span className="px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 animate-pulse">{urgentN} urgent</span>}
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200"><CheckCircle2 className="w-4 h-4" /> Semua aman, tidak ada yang perlu ditindaklanjuti.</div>
      ) : (
        <div>
          <div className="grid lg:grid-cols-2 gap-1.5" data-testid="home-alerts">
            {shown.map((a, i) => {
              const L = LEVEL[a.level]; const Icon = L.icon;
              return (
                <button key={i} type="button" onClick={a.onClick} title={`${a.title} - ${a.desc}`} className={`text-left rounded-lg border px-2.5 py-1.5 flex items-center gap-2 min-w-0 hover:brightness-125 transition ${L.cls}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${L.ic}`} />
                  <span className={`text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded flex-shrink-0 ${L.badge}`}>{L.label}</span>
                  <span className="text-[12px] font-bold text-white whitespace-nowrap">{a.title}</span>
                  <span className="text-[11px] text-white/45 truncate flex-1 min-w-0">{a.desc}</span>
                  <span className="text-[9px] font-black uppercase tracking-wider text-white/70 whitespace-nowrap inline-flex items-center flex-shrink-0">{a.action}<ChevronRight className="w-3 h-3" /></span>
                </button>
              );
            })}
          </div>
          {alerts.length > SHOW && (
            <button type="button" onClick={() => setAll((v) => !v)} className="mt-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50 hover:text-white inline-flex items-center gap-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${all ? 'rotate-180' : ''}`} /> {all ? 'Ringkas' : `Lihat semua (${alerts.length})`}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-1.5" data-testid="home-shortcuts">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.label} type="button" onClick={t.go} className="rounded-lg bg-white/[0.03] border border-white/10 hover:border-white/25 hover:bg-white/[0.06] px-2.5 py-2 text-left flex items-center gap-2 min-w-0 transition">
              <span className={`w-7 h-7 rounded-lg grid place-items-center flex-shrink-0 ${t.n ? (t.hot ? 'bg-red-500 text-white' : 'bg-[#F5C518] text-black') : 'bg-white/5 text-white/80'}`}><Icon className="w-3.5 h-3.5" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-bold text-white leading-tight truncate">{t.label}</span>
                <span className="block text-[10px] text-white/40 truncate">{t.sub}</span>
              </span>
              {t.n > 0 && <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 ${t.hot ? 'bg-red-500 text-white' : 'bg-[#F5C518] text-black'}`}>{t.n}</span>}
            </button>
          );
        })}
      </div>
      {showStamp && <StampRallyAdmin onClose={() => setShowStamp(false)} />}
    </div>
  );
}
